<template>
  <div class="space-y-6">
    <!-- Connection Status -->
    <div class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <img 
            src="@/assets/integrations/meta-icon.webp" 
            alt="Meta Logo" 
            class="h-12 w-12 bg-white rounded-token-sm p-1 object-contain" 
          />
          <div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-50">Meta Pixel</h3>
            <p class="text-sm text-gray-600 dark:text-gray-300">Track conversions and optimize your Facebook/Instagram advertising campaigns</p>
          </div>
        </div>
        <div class="flex items-center space-x-4">
          <span
            :class="[
              isConnected ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
              'px-3 py-1 rounded-token-sm text-sm font-medium'
            ]"
          >
            {{ isConnected ? 'Connected' : 'Not Connected' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Meta Pixel Configuration -->
    <div class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-6">
      <form @submit.prevent="onSave">
        <div class="space-y-4">
          <div>
            <label for="facebookPixel" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Meta Pixel ID
            </label>
            <input
              id="facebookPixel"
              type="text"
              v-model="facebookPixel"
              autocomplete="off"
              class="block w-full rounded-token-sm border-hairline py-2.5 pl-3 pr-3 text-gray-900 dark:text-gray-50 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-1 focus:ring-inset focus:ring-[var(--theme-primary-500)] focus:border-[var(--theme-primary-500)] sm:text-sm sm:leading-6 font-semibold bg-white dark:bg-gray-stack-2"
              :class="facebookPixel.length > 5 ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700' : ''"
              placeholder="Enter your Pixel ID (e.g., 723652235132153)"
              pattern="[0-9]+"
              maxlength="20"
            />
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Your Meta Pixel ID is a numeric identifier. Find it in your Meta Events Manager.
            </p>
          </div>

          <!-- Conversions API Access Token -->
          <div>
            <label for="facebookPixelAccessToken" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Conversions API Access Token
              <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">(Optional but recommended)</span>
            </label>
            <div class="relative">
              <input
                id="facebookPixelAccessToken"
                :type="showToken ? 'text' : 'password'"
                v-model="facebookPixelAccessToken"
                autocomplete="off"
                class="block w-full rounded-token-sm border-hairline py-2.5 pl-3 pr-10 text-gray-900 dark:text-gray-50 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-1 focus:ring-inset focus:ring-[var(--theme-primary-500)] focus:border-[var(--theme-primary-500)] sm:text-sm sm:leading-6 font-semibold bg-white dark:bg-gray-stack-2 font-mono text-xs"
                :class="facebookPixelAccessToken.length > 20 ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700' : ''"
                placeholder="Enter your Conversions API access token"
              />
              <button
                type="button"
                @click="showToken = !showToken"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Show/hide token"
              >
                <svg v-if="showToken" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.532 13.532l-3.29-3.29M3 3l18 18" />
                </svg>
                <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
            <div class="mt-2 flex items-center justify-between">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Enables server-side event tracking for better accuracy and privacy compliance.
              </p>
              <button
                type="button"
                @click="showAccessTokenHelp = !showAccessTokenHelp"
                class="text-sm text-[var(--theme-primary-600)] dark:text-[var(--theme-primary-400)] hover:underline"
              >
                How to get token
              </button>
            </div>
            
            <!-- Help Section -->
            <div v-if="showAccessTokenHelp" class="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-token-sm">
              <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">How to get your Conversions API Access Token:</h4>
              <ol class="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-300">
                <li>Go to <a href="https://business.facebook.com/events_manager2/" target="_blank" class="underline font-semibold">Meta Events Manager</a></li>
                <li>Select your Pixel</li>
                <li>Click on the "Settings" tab</li>
                <li>Scroll to the "Conversions API" section</li>
                <li>Click "Generate access token"</li>
                <li>Copy the token and paste it above</li>
              </ol>
              <p class="mt-2 text-xs text-blue-700 dark:text-blue-400">
                <strong>What is the Conversions API?</strong> It's Meta's server-side API that sends events directly from your server to Meta (Facebook & Instagram). This provides better tracking accuracy, helps with privacy compliance (iOS 14.5+, ad blockers), and ensures events are tracked even when JavaScript is blocked.
              </p>
            </div>
          </div>

          <!-- Save Button -->
          <div class="pt-4 border-t border-hairline">
            <button
              type="submit"
              :disabled="isSaving || !facebookPixel || facebookPixel.length < 5"
              class="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gray-stack-1 dark:bg-gray-stack-2 border border-hairline rounded-token-sm shadow-3d hover:bg-gray-stack-2 dark:hover:bg-gray-stack-3 focus:ring-[color:var(--ring-primary)] ring-2 ring-offset-2 transition-fast disabled:opacity-50 disabled:cursor-not-allowed"
              :style="{ backgroundColor: isSaving ? 'var(--theme-primary-400)' : 'var(--theme-primary-500)' }"
            >
              <svg
                v-if="isSaving"
                class="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647zM20 12c0-3.042-1.135-5.824-3-7.938l-3 2.647A7.962 7.962 0 0120 12h4c0-6.627-5.373-12-12-12v4c3.042 0 5.824 1.135 7.938 3l-2.647 3zM9.171 4.757l-2.828 2.828 1.415 1.414 2.828-2.828-1.415-1.414zM14.828 19.243l2.828-2.828-1.414-1.414-2.828 2.828 1.414 1.414z"
                ></path>
              </svg>
              <CheckCircleIcon
                v-else-if="saveSuccess"
                class="h-5 w-5 text-green-500 mr-2"
                aria-hidden="true"
              />
              <span v-if="isSaving">Saving Configuration...</span>
              <span v-else-if="saveSuccess">Configuration Saved!</span>
              <span v-else>Save Configuration</span>
            </button>
            
            <!-- Success Message -->
            <div v-if="saveSuccess" class="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-token-sm">
              <p class="text-sm text-green-800 dark:text-green-300">
                ✓ Meta Pixel configuration saved successfully. Your Pixel ID and access token have been updated.
              </p>
            </div>
          </div>

          <!-- Last Updated Timestamp -->
          <div v-if="lastUpdated" class="pt-3 border-t border-hairline">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Last updated: {{ formatDate(lastUpdated) }}
            </p>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { CheckCircleIcon } from "@heroicons/vue/outline";
import { projectFirestore } from "../../../firebase/config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import store from "../../../store";

const facebookPixel = ref("");
const facebookPixelAccessToken = ref("");
const isSaving = ref(false);
const lastUpdated = ref(null);
const showAccessTokenHelp = ref(false);
const showToken = ref(false);
const saveSuccess = ref(false);
const isConnected = computed(() => facebookPixel.value && facebookPixel.value.length > 5);

// Get company profile reference
const getCompanyProfileRef = () => {
  if (!store.state.companyProfileName) {
    throw new Error("Company profile name not found");
  }
  return doc(projectFirestore, "companyProfile", store.state.companyProfileName);
};

// Get user document reference
const getUserRef = () => {
  if (!store.state.currentUser?.uid) {
    throw new Error("User UID not found");
  }
  return doc(projectFirestore, "users", store.state.currentUser.uid);
};

// Load existing pixel ID and access token
onMounted(async () => {
  try {
    // Load pixel ID from companyProfile (public)
    const companyProfileRef = getCompanyProfileRef();
    const companyProfileSnap = await getDoc(companyProfileRef);
    
    if (companyProfileSnap.exists()) {
      const companyData = companyProfileSnap.data();
      facebookPixel.value = companyData.config?.facebookPixel || "";
      lastUpdated.value = companyData.config?.facebookPixelUpdated?.toDate?.() || 
                          (companyData.config?.facebookPixelUpdated ? new Date(companyData.config.facebookPixelUpdated) : null);
    }

    // Load access token from users collection (secure)
    const userRef = getUserRef();
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      facebookPixelAccessToken.value = userData.config?.facebookPixelAccessToken || userData.facebookPixelAccessToken || "";
    }
  } catch (error) {
    console.error("Error fetching Meta Pixel configuration:", error);
  }
});

// Save pixel ID and access token
const onSave = async () => {
  if (!facebookPixel.value || facebookPixel.value.length < 5) {
    return;
  }

  isSaving.value = true;
  saveSuccess.value = false;
  
  try {
    // Save pixel ID to companyProfile (public, but safe)
    const companyProfileRef = getCompanyProfileRef();
    await updateDoc(companyProfileRef, {
      "config.facebookPixel": facebookPixel.value.trim(),
      "config.facebookPixelUpdated": new Date()
    });
    
    // Save access token to users collection (secure, requires authentication)
    if (facebookPixelAccessToken.value && facebookPixelAccessToken.value.trim().length > 0) {
      const userRef = getUserRef();
      await updateDoc(userRef, {
        "config.facebookPixelAccessToken": facebookPixelAccessToken.value.trim()
      });
    }
    
    lastUpdated.value = new Date();
    saveSuccess.value = true;
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      saveSuccess.value = false;
    }, 3000);
  } catch (error) {
    console.error("Error saving Meta Pixel:", error);
    alert("Failed to save Meta Pixel configuration. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

// Format date for display
const formatDate = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString();
};

// Expose for parent component
defineExpose({
  isConnected,
  refreshData: async () => {
    try {
      // Load pixel ID from companyProfile
      const companyProfileRef = getCompanyProfileRef();
      const companyProfileSnap = await getDoc(companyProfileRef);
      
      if (companyProfileSnap.exists()) {
        const companyData = companyProfileSnap.data();
        facebookPixel.value = companyData.config?.facebookPixel || "";
        lastUpdated.value = companyData.config?.facebookPixelUpdated?.toDate?.() || 
                            (companyData.config?.facebookPixelUpdated ? new Date(companyData.config.facebookPixelUpdated) : null);
      }

      // Load access token from users collection
      const userRef = getUserRef();
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        facebookPixelAccessToken.value = userData.config?.facebookPixelAccessToken || userData.facebookPixelAccessToken || "";
      }
    } catch (error) {
      console.error("Error refreshing Meta Pixel data:", error);
    }
  }
});
</script>

